// ERRORS-1 Part B: the "Report a bug" dialog (docs/specs/errors-1.md §7).
//
// Two steps, on purpose: the person writes, then REVIEWS the exact server-scrubbed issue
// (POST /support/bug-reports/preview) before anything is stored. What they review is what is
// filed — same builder server-side. Diagnostics are the allowlisted set from
// lib/diagnostics.ts; there is no way to attach transcript text, titles, emails or audio.
import { useId, useMemo, useState } from 'react';
import { ScribeApiError } from '@scribe/core/client';
import { getRecordingProblem, problemCodeForApiError } from '@scribe/core/errors';
import type { BugReport, BugReportPreview, CreateBugReportRequest } from '@scribe/core/types';
import { Modal } from './ui';
import { collectDiagnostics } from '../lib/diagnostics';
import type { BugReportPrefill } from '../lib/bugReport';
import { useCreateBugReport, usePreviewBugReport } from '../lib/supportQueries';

function newIdempotencyKey(): string {
  return typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `k-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function errorCopy(err: unknown): string {
  if (err instanceof ScribeApiError && err.code === 'contains_transcript_text') return err.message;
  return getRecordingProblem(problemCodeForApiError(err, navigator.onLine)).title;
}

export function BugReportDialog({
  prefill,
  onClose,
}: {
  prefill: BugReportPrefill;
  onClose: () => void;
}) {
  const titleId = useId();
  const [whatHappened, setWhatHappened] = useState(prefill.summary ?? '');
  const [expected, setExpected] = useState('');
  const [steps, setSteps] = useState('');
  const [preview, setPreview] = useState<BugReportPreview | null>(null);
  const [sent, setSent] = useState<BugReport | null>(null);
  // One key per dialog: a double-click (or a retry after a flaky response) files ONE report.
  const idempotencyKey = useMemo(newIdempotencyKey, []);
  const previewMutation = usePreviewBugReport();
  const createMutation = useCreateBugReport();

  function request(): CreateBugReportRequest {
    return {
      what_happened: whatHappened.trim(),
      ...(expected.trim() ? { expected: expected.trim() } : {}),
      ...(steps.trim() ? { steps: steps.trim() } : {}),
      ...(prefill.recordingId ? { recording_id: prefill.recordingId } : {}),
      ...(prefill.failureCode ? { failure_code: prefill.failureCode } : {}),
      ...(prefill.area ? { area: prefill.area } : {}),
      diagnostics: collectDiagnostics(),
      idempotency_key: idempotencyKey,
    };
  }

  const canReview = whatHappened.trim().length >= 3 && !previewMutation.isPending;

  return (
    <Modal onClose={onClose} labelledBy={titleId} wide>
      <h2 id={titleId}>Report a problem</h2>
      {sent ? (
        <div role="status">
          <p>
            {sent.github_issue_number != null
              ? `Thanks — reported as #${sent.github_issue_number}.`
              : 'Thanks — your report was received. It will be filed as an issue for the team shortly.'}
          </p>
          <div className="modal-actions">
            <button type="button" onClick={onClose}>
              Done
            </button>
          </div>
        </div>
      ) : preview ? (
        <>
          <p className="muted">
            This is exactly what will be sent. It never includes transcript text, recording
            titles, names, emails or audio — you’re identified only by an anonymous id.
          </p>
          <div className="bug-preview" aria-label="Report preview">
            <p>
              <strong>{preview.title}</strong>
            </p>
            <p className="muted">Labels: {preview.labels.join(', ')}</p>
            <pre className="bug-preview-body">{preview.body}</pre>
          </div>
          {createMutation.isError && <p className="error">{errorCopy(createMutation.error)}</p>}
          <div className="modal-actions">
            <button type="button" className="ghost" onClick={() => setPreview(null)}>
              Back
            </button>
            <button
              type="button"
              disabled={createMutation.isPending}
              onClick={() => createMutation.mutate(request(), { onSuccess: setSent })}
            >
              {createMutation.isPending ? 'Sending…' : 'Send report'}
            </button>
          </div>
        </>
      ) : (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (canReview) previewMutation.mutate(request(), { onSuccess: setPreview });
          }}
        >
          <p className="muted">
            Describe the problem in your own words. Please don’t paste anything that was said in
            a meeting.
          </p>
          <label className="field">
            <span className="label">What happened?</span>
            <textarea
              required
              maxLength={2000}
              rows={4}
              value={whatHappened}
              onChange={(e) => setWhatHappened(e.target.value)}
            />
          </label>
          <label className="field">
            <span className="label">What did you expect?</span>
            <textarea maxLength={2000} rows={2} value={expected} onChange={(e) => setExpected(e.target.value)} />
          </label>
          <label className="field">
            <span className="label">Steps to reproduce (optional)</span>
            <textarea maxLength={2000} rows={3} value={steps} onChange={(e) => setSteps(e.target.value)} />
          </label>
          {previewMutation.isError && <p className="error">{errorCopy(previewMutation.error)}</p>}
          <div className="modal-actions">
            <button type="button" className="ghost" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" disabled={!canReview}>
              {previewMutation.isPending ? 'Preparing…' : 'Review what will be sent'}
            </button>
          </div>
        </form>
      )}
    </Modal>
  );
}
